<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import ArrowDownIcon from '@/assets/icons/ArrowDownIcon.vue';
import ArrowRightIcon from '@/assets/icons/ArrowRightIcon.vue';
import CompetencesIcon from '@/assets/icons/CompetencesIcon.vue';
import ExternalMaterialIcon from '@/assets/icons/ExternalMaterialIcon.vue';
import GroupIcon from '@/assets/icons/GroupIcon.vue';
import { useCompetenceAssessment } from '@/composables/competenceAssessment/useCompetenceAssessment';
import {
  SupportMeasureProtocolRequiredAction,
  type TSupportMeasureProtocolRequiredAction,
} from '@/composables/supportMeasureEntry/supportMeasureProtocol.validation';
import type { IRequiredActionPrompt } from '@/composables/requiredAction/requiredAction.types';
import { useRequiredActionGuidance } from '@/composables/requiredAction/useRequiredActionGuidance';
import { useSupportMeasureProtocolDraft } from '@/composables/supportMeasureEntry/useSupportMeasureProtocolDraft';
import {
  ESupportMeasureProtocolSuggestionKind,
  type ISupportMeasureProtocolSuggestionItem,
  useSupportMeasureProtocolSuggestions,
} from '@/composables/supportMeasureEntry/useSupportMeasureProtocolSuggestions';
import { useCompetenceDomain } from '@/composables/useCompetenceDomain';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useSupportMeasureEntry } from '@/composables/supportMeasureEntry/useSupportMeasureEntry';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useMobile } from '@/composables/useMobile';
import ComposerActionRow from '@/core/elements/ComposerActionRow.vue';
import ComposerChip from '@/core/elements/ComposerChip.vue';
import ComposerContextRow from '@/core/elements/ComposerContextRow.vue';
import ComposerInputRow from '@/core/elements/ComposerInputRow.vue';
import ComposerPanel from '@/core/elements/ComposerPanel.vue';
import ComposerTextInput from '@/core/elements/ComposerTextInput.vue';
import CustomSeparator from '@/core/elements/CustomSeparator.vue';
import SuggestionPanel from '@/core/elements/SuggestionPanel.vue';
import { getSuggestionOptionId } from '@/core/elements/suggestionPanel.utils';
import SelectedCompetenceRatingRow from '@/core/competences/SelectedCompetenceRatingRow.vue';
import CompetenceSelectionPopover from '@/core/selects/CompetenceSelectionPopover.vue';
import DateSelectionPopover from '@/core/selects/DateSelectionPopover.vue';
import ExternalMaterialSelectionPopover from '@/core/supportMeasures/ExternalMaterialSelectionPopover.vue';
import ExternalMaterialRow from '@/core/supportMeasures/ExternalMaterialRow.vue';
import UserAvatar from '@/core/UserAvatar.vue';
import type { TStudentLike } from '@/interfaces';
import { resolveDialogTeleportTarget } from '@/utils/dom.utils';
import { EKeyboardKey } from '@/utils/menu.utils';
import { SUPPORT_MEASURE_PROTOCOL_TEXT_MAX_LENGTH } from 'common/constants/support.measure.constants';
import { onClickOutside } from '@vueuse/core';
import { computed, nextTick, ref, useTemplateRef, type Component } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  supportMeasureEntryId: number;
  schoolSubjectId: number;
  schoolClassId: number | null;
  students: Components.Schemas.BaseStudentDto[];
  // the timeline filter selection — preselected as the composer's target student
  selectedStudentId: number | null;
  // clicks inside this element (the sidebar student filter) must NOT collapse the composer
  ignoreClickOutsideEl?: HTMLElement | null;
  // competences currently linked to the measure entry (the rating picker shows these by default)
  connectedCompetenceIds: number[];
  // when set, the composer edits this entry in place (prefilled) and saves via updateProtocolEntry
  editProtocolEntry?: Components.Schemas.SupportMeasureProtocolEntryDto | null;
}>();

const emit = defineEmits<{
  // edit mode finished (saved or cancelled) — the parent stops rendering the composer
  saved: [];
  cancel: [];
}>();

// Participants of the edited entry that already left the measure: they stay selectable here — that is the only
// place their documentation can be removed — but they cannot be added to a new entry.
const historicalParticipants = computed<Components.Schemas.BaseStudentDto[]>(() => {
  const memberIds = new Set(props.students.map(({ id }) => id));
  return (props.editProtocolEntry?.students ?? []).filter((student) => !memberIds.has(student.id));
});
const selectableStudents = computed<Components.Schemas.BaseStudentDto[]>(() => [
  ...props.students,
  ...historicalParticipants.value,
]);

const SUGGESTION_LISTBOX_ID_PREFIX = 'support-measure-protocol-suggestions';

const COMMAND_ICON_BY_KIND: Partial<Record<ISupportMeasureProtocolSuggestionItem['kind'], Component>> = {
  [ESupportMeasureProtocolSuggestionKind.OpenCompetences]: CompetencesIcon,
  [ESupportMeasureProtocolSuggestionKind.OpenStudents]: GroupIcon,
  [ESupportMeasureProtocolSuggestionKind.OpenMaterials]: ExternalMaterialIcon,
};

const isEditMode = computed<boolean>(() => props.editProtocolEntry != null);
const { t } = useI18n();
const { confirm } = useConfirmDialog();
const { isMobile } = useMobile();

const { createProtocolEntry, updateProtocolEntry, addCompetences } = useSupportMeasureEntry();
const { allCompetenceDomains } = useCompetenceDomain();
const { allSchoolSubjects, getSchoolSubjectColor } = useSchoolSubject();
const { currentCompetenceAssessment } = useCompetenceAssessment();

const rootRef = useTemplateRef('rootRef');
const textareaRef = useTemplateRef('textareaRef');
const contextRowRef = useTemplateRef('contextRowRef');
const materialTriggerRef = ref<HTMLElement | null>(null);
const materialCommandAnchorRef = useTemplateRef<HTMLElement>('materialCommandAnchorRef');
const materialPopoverRef = useTemplateRef('materialPopoverRef');
const competenceTriggerRef = ref<HTMLElement | null>(null);
const competenceCommandAnchorRef = useTemplateRef<HTMLElement>('competenceCommandAnchorRef');
const competencePopoverRef = useTemplateRef('competencePopoverRef');
const dateTriggerRef = ref<HTMLElement | null>(null);
const datePopoverRef = useTemplateRef('datePopoverRef');

const isExpanded = ref(isEditMode.value);
const isSaving = ref(false);
const mobileViewportPresentation = computed<boolean>(() => isMobile.value);
const mobileViewportExpanded = computed<boolean>(() => mobileViewportPresentation.value && isExpanded.value);
const suggestionsEnabled = computed(() => !mobileViewportPresentation.value);
const {
  addCompetence,
  addMaterial,
  addStudent,
  allStudentsSelected,
  buildObservations,
  canSubmit,
  canRateCompetences,
  competenceAssessment,
  date,
  isPristine,
  levelByCompetenceId,
  newlyConnectedCompetenceIds,
  nextValidationIssue,
  removeCompetence,
  removeMaterial,
  resetAfterCreate,
  resolvedTargetStudentIds,
  selectAllStudents: onSelectAllStudents,
  selectedCompetenceIds,
  selectedCompetenceRows,
  selectedCompetences,
  selectedMaterials,
  selectedStudents,
  setCompetenceLevel: setLevel,
  setStudents: onUpdateStudents,
  subjectDomains,
  text,
} = useSupportMeasureProtocolDraft({
  allCompetenceDomains,
  connectedCompetenceIds: () => props.connectedCompetenceIds,
  currentCompetenceAssessment,
  editProtocolEntry: () => props.editProtocolEntry,
  historicalStudentIds: () => historicalParticipants.value.map(({ id }) => id),
  schoolSubjectId: () => props.schoolSubjectId,
  selectedStudentId: () => props.selectedStudentId,
  students: () => selectableStudents.value,
});

const suggestionListboxId = computed<string>(() =>
  props.editProtocolEntry
    ? `${SUGGESTION_LISTBOX_ID_PREFIX}-edit-${props.editProtocolEntry.id}`
    : `${SUGGESTION_LISTBOX_ID_PREFIX}-create`,
);
const suggestionStudents = computed<TStudentLike[]>(() => selectableStudents.value);
const suggestionSelectedStudentIds = computed<number[]>(() =>
  allStudentsSelected.value ? [] : resolvedTargetStudentIds.value,
);
const suggestions = useSupportMeasureProtocolSuggestions(
  {
    canSelectCompetences: canRateCompetences,
    selectedStudentIds: suggestionSelectedStudentIds,
    students: suggestionStudents,
  },
  { addStudent },
  { enabled: suggestionsEnabled },
);
const activeSuggestionOptionId = computed<string | null>(() =>
  suggestions.isOpen.value ? getSuggestionOptionId(suggestionListboxId.value, suggestions.activeIndex.value) : null,
);

const competenceChipTintColor = computed<string | undefined>(() =>
  selectedCompetenceIds.value.length ? getSchoolSubjectColor(props.schoolSubjectId) : undefined,
);
const competenceChipLabel = computed<string>(() =>
  selectedCompetenceIds.value.length
    ? t('competence.count', { count: selectedCompetenceIds.value.length })
    : t('general.competences'),
);

const requirementGuidance = useRequiredActionGuidance(nextValidationIssue, t);
const { announcement } = requirementGuidance;
const textPrompt = requirementGuidance.promptForAction(SupportMeasureProtocolRequiredAction.Text);
const studentsPrompt = requirementGuidance.promptForAction(SupportMeasureProtocolRequiredAction.Students);
const competenceRatingPrompt = (competenceId: number): IRequiredActionPrompt | null =>
  requirementGuidance.getPrompt(
    (issue) =>
      issue.action === SupportMeasureProtocolRequiredAction.CompetenceRating && issue.competenceId === competenceId,
  );
// Keep the picker in the fullscreen composer dialog so it remains above the modal top layer.
const datePickerTeleport = computed<boolean | HTMLElement>(() =>
  resolveDialogTeleportTarget(dateTriggerRef.value, rootRef.value),
);

const anyPopoverOpen = (): boolean =>
  !!materialPopoverRef.value?.isOpen ||
  !!competencePopoverRef.value?.isOpen ||
  !!datePopoverRef.value?.isOpen ||
  !!contextRowRef.value?.isStudentsOpen;

const expand = async (): Promise<void> => {
  isExpanded.value = true;
  await nextTick();
  if (mobileViewportPresentation.value) return;
  textareaRef.value?.focus();
};
const handlePanelOpened = (): void => textareaRef.value?.focus();
const collapse = (): void => {
  requirementGuidance.reset();
  isExpanded.value = false;
  suggestions.close();
};

const handleCancel = (): void => {
  requirementGuidance.reset();
  emit('cancel');
};

const closeOpenSelection = (): boolean => {
  if (suggestions.isOpen.value) {
    suggestions.close();
    return true;
  }
  if (contextRowRef.value?.isStudentsOpen) {
    contextRowRef.value.hideStudents();
    return true;
  }
  if (materialPopoverRef.value?.isOpen) {
    materialPopoverRef.value.hide();
    return true;
  }
  if (competencePopoverRef.value?.isOpen) {
    competencePopoverRef.value.hide();
    return true;
  }
  if (datePopoverRef.value?.isOpen) {
    datePopoverRef.value.hide();
    return true;
  }
  return false;
};

const handleRequestCollapse = async (event?: Event): Promise<void> => {
  event?.preventDefault();
  event?.stopPropagation();

  // closing mid-save would drop the composer while the request is still in flight
  if (isSaving.value) return;
  if (closeOpenSelection()) return;
  if (isEditMode.value) {
    handleCancel();
    return;
  }
  if (isPristine.value) {
    collapse();
    return;
  }

  const discard = await confirm({
    title: t('quickEntry.confirmDiscard.title'),
    description: t('quickEntry.confirmDiscard.description'),
    confirmButtonLabel: t('quickEntry.confirmDiscard.confirmButtonLabel'),
    declineButtonLabel: t('quickEntry.confirmDiscard.declineButtonLabel'),
    theme: 'danger',
  });
  if (!discard) return;
  resetAfterCreate();
  collapse();
};

const handleRequirementInteraction = (action: TSupportMeasureProtocolRequiredAction): void => {
  requirementGuidance.interactWithAction(action);
};

const handleCompetenceRatingInteraction = (competenceId: number): void => {
  requirementGuidance.interact(
    (issue) =>
      issue.action === SupportMeasureProtocolRequiredAction.CompetenceRating && issue.competenceId === competenceId,
  );
};

const handleTextChange = (value: string): void => {
  handleRequirementInteraction(SupportMeasureProtocolRequiredAction.Text);
  text.value = value;
};

const handleCompetenceLevelChange = (competenceId: number, value: number | null): void => {
  handleCompetenceRatingInteraction(competenceId);
  setLevel(competenceId, value);
};

const handleTextInputKeydown = (event: KeyboardEvent): void => {
  if (event.key === EKeyboardKey.Enter && (event.metaKey || event.ctrlKey)) {
    event.preventDefault();
    void submit();
    return;
  }
  if (suggestions.isOpen.value && (event.key === EKeyboardKey.Enter || event.key === EKeyboardKey.Tab)) {
    event.preventDefault();
    void acceptSuggestion(suggestions.items.value[suggestions.activeIndex.value]);
    return;
  }
  suggestions.handleSuggestionKeydown(event);
};

const handleCaretChange = (caretIndex: number): void => {
  suggestions.updateFromInput(text.value, caretIndex);
};

const handleStudentSelectionShow = (): void => suggestions.close();

const acceptSuggestion = async (item: ISupportMeasureProtocolSuggestionItem): Promise<void> => {
  const result = suggestions.acceptItem(item);
  if (!result) return;
  await textareaRef.value?.setTextAndCaret(result.text, result.caretIndex);
  suggestions.updateFromInput(result.text, result.caretIndex);

  if (item.kind === ESupportMeasureProtocolSuggestionKind.OpenStudents) {
    await contextRowRef.value?.showStudents();
  } else if (item.kind === ESupportMeasureProtocolSuggestionKind.OpenCompetences && competenceCommandAnchorRef.value) {
    toggleCompetencePopover(competenceCommandAnchorRef.value);
  } else if (item.kind === ESupportMeasureProtocolSuggestionKind.OpenMaterials && materialCommandAnchorRef.value) {
    toggleMaterialPopover(materialCommandAnchorRef.value);
  }
};

const toggleMaterialPopover = (anchor: HTMLElement): void => {
  suggestions.close();
  materialTriggerRef.value = anchor;
  if (materialPopoverRef.value?.isOpen) materialPopoverRef.value.hide();
  else void nextTick(() => materialPopoverRef.value?.show());
};
const toggleCompetencePopover = (anchor: HTMLElement): void => {
  suggestions.close();
  competenceTriggerRef.value = anchor;
  if (competencePopoverRef.value?.isOpen) competencePopoverRef.value.hide();
  else void nextTick(() => competencePopoverRef.value?.show());
};
const onCompetenceChipClick = (anchor: HTMLElement): void => {
  toggleCompetencePopover(anchor);
};
const toggleDatePopover = (anchor: HTMLElement): void => {
  suggestions.close();
  dateTriggerRef.value = anchor;
  if (datePopoverRef.value?.isOpen) datePopoverRef.value.hide();
  else void nextTick(() => datePopoverRef.value?.show());
};

type TProtocolSaveResult = 'created' | 'updated';

const saveProtocolEntry = async (): Promise<TProtocolSaveResult> => {
  // Connect first so its activity sorts below the protocol entry; silent keeps one success notification.
  if (newlyConnectedCompetenceIds.value.length) {
    await addCompetences(props.supportMeasureEntryId, newlyConnectedCompetenceIds.value, { silent: true });
  }
  const externalMaterialIds = selectedMaterials.value.map((material) => material.externalMaterialId);
  if (props.editProtocolEntry) {
    await updateProtocolEntry(props.editProtocolEntry.id, {
      text: text.value.trim(),
      date: date.value,
      studentIds: resolvedTargetStudentIds.value,
      externalMaterialIds,
      competenceObservations: buildObservations(),
    });
    return 'updated';
  }
  await createProtocolEntry(props.supportMeasureEntryId, {
    text: text.value.trim(),
    date: date.value,
    studentIds: allStudentsSelected.value ? undefined : resolvedTargetStudentIds.value,
    externalMaterialIds,
    competenceObservations: buildObservations(),
  });
  return 'created';
};

const finishProtocolSave = async (result: TProtocolSaveResult): Promise<void> => {
  if (result === 'updated') {
    emit('saved');
    return;
  }
  resetAfterCreate();
  if (mobileViewportExpanded.value) {
    collapse();
    return;
  }
  await nextTick();
  textareaRef.value?.focus();
};

const submit = async (): Promise<void> => {
  if (isSaving.value) return;
  if (!canSubmit.value) {
    const issue = await requirementGuidance.requestNext();
    if (issue?.action === SupportMeasureProtocolRequiredAction.Text) textareaRef.value?.focus();
    return;
  }

  suggestions.close();
  requirementGuidance.reset();
  isSaving.value = true;
  try {
    await finishProtocolSave(await saveProtocolEntry());
  } finally {
    isSaving.value = false;
  }
};

// changing the sidebar filter (outside rootRef) must keep an empty composer open — ignore those clicks
const clickOutsideIgnore = computed(() => props.ignoreClickOutsideEl ?? undefined);
onClickOutside(
  rootRef,
  () => {
    // The fullscreen dialog owns its backdrop and Escape behavior. Its selection drawers are teleported
    // outside rootRef, so desktop click-outside handling would otherwise collapse the composer as a drawer closes.
    if (mobileViewportPresentation.value) return;
    if (isEditMode.value || !isExpanded.value || anyPopoverOpen() || isSaving.value) return;
    if (isPristine.value) collapse();
  },
  { ignore: [clickOutsideIgnore] },
);
</script>

<template>
  <section ref="rootRef" @keydown.esc="handleRequestCollapse">
    <ComposerPanel
      :accessible-label="$t('supportMeasureEntry.protocolPlaceholder')"
      :collapsed-label="$t('supportMeasureEntry.protocolPlaceholder')"
      compact
      :expanded="isExpanded"
      :flat="isEditMode"
      :mobile-viewport="mobileViewportPresentation"
      @expand="expand"
      @opened="handlePanelOpened"
      @request-collapse="handleRequestCollapse"
    >
      <ComposerContextRow
        ref="contextRowRef"
        :all-eligible-students-selected="allStudentsSelected"
        :eligible-students="selectableStudents"
        :selected-students="selectedStudents"
        :date="date"
        :date-popover-open="!!datePopoverRef?.isOpen"
        :mobile-drawer="mobileViewportPresentation"
        :students-prompt="studentsPrompt"
        :show-close="!mobileViewportExpanded"
        :close-disabled="isSaving"
        @close="handleRequestCollapse"
        @show:students="handleStudentSelectionShow"
        @interact:students="handleRequirementInteraction(SupportMeasureProtocolRequiredAction.Students)"
        @select:all-eligible-students="onSelectAllStudents"
        @toggle:date="toggleDatePopover"
        @update:students="onUpdateStudents"
      />

      <div :class="mobileViewportExpanded && 'flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain'">
        <ComposerInputRow
          :fill-available-height="mobileViewportExpanded"
          :required-action-prompt="textPrompt"
          @interact:required-action="handleRequirementInteraction(SupportMeasureProtocolRequiredAction.Text)"
        >
          <ComposerTextInput
            ref="textareaRef"
            :fill-height="mobileViewportExpanded"
            :model-value="text"
            :placeholder="$t('supportMeasureEntry.protocolPlaceholder')"
            :max-length="SUPPORT_MEASURE_PROTOCOL_TEXT_MAX_LENGTH"
            :listbox-id="suggestionsEnabled ? suggestionListboxId : undefined"
            :listbox-open="suggestions.isOpen.value"
            :active-option-id="activeSuggestionOptionId"
            @update:model-value="handleTextChange"
            @caret-change="handleCaretChange"
            @keydown="handleTextInputKeydown"
          />
          <SuggestionPanel
            v-if="suggestions.isOpen.value"
            :listbox-id="suggestionListboxId"
            :options="suggestions.items.value"
            :active-index="suggestions.activeIndex.value"
            @select="acceptSuggestion"
            @update:active-index="suggestions.activeIndex.value = $event"
          >
            <template #leading="{ option }">
              <UserAvatar
                v-if="option.kind === ESupportMeasureProtocolSuggestionKind.Student && option.student"
                class="!size-icon-md"
                :user="option.student"
              />
              <span
                v-else-if="COMMAND_ICON_BY_KIND[option.kind]"
                class="flex size-icon-md shrink-0 items-center justify-center text-primary"
              >
                <component :is="COMMAND_ICON_BY_KIND[option.kind]" class="size-icon-sm" aria-hidden="true" />
              </span>
            </template>
          </SuggestionPanel>
        </ComposerInputRow>

        <!-- The fullscreen composer has one scroll owner for text, ratings and apps. Desktop keeps the
             existing bounded sub-list so its pinned action row remains reachable. -->
        <div
          v-if="(selectedCompetenceRows.length && competenceAssessment) || selectedMaterials.length"
          class="mt-md flex flex-col gap-md"
          :class="!mobileViewportExpanded && 'max-h-composer-selection-list overflow-y-auto'"
        >
          <div
            v-if="selectedCompetenceRows.length && competenceAssessment"
            class="rounded-2xl bg-surface-secondary py-sm"
          >
            <template v-for="(row, index) in selectedCompetenceRows" :key="row.competence.id">
              <div v-if="index > 0" class="w-full px-md py-sm"><CustomSeparator /></div>
              <SelectedCompetenceRatingRow
                :selected-competence="row"
                :assessment="competenceAssessment"
                :value="levelByCompetenceId[row.competence.id] ?? null"
                :rating-prompt="competenceRatingPrompt(row.competence.id)"
                @interact:rating="handleCompetenceRatingInteraction(row.competence.id)"
                @update:value="handleCompetenceLevelChange(row.competence.id, $event)"
                @remove="removeCompetence(row.competence.id)"
              />
            </template>
          </div>

          <!-- apps rendered as one list with dividers, matching the submitted protocol entry -->
          <div v-if="selectedMaterials.length" class="rounded-2xl bg-surface-secondary py-sm">
            <template v-for="(material, index) in selectedMaterials" :key="material.externalMaterialId">
              <div v-if="index > 0" class="w-full px-md py-sm"><CustomSeparator /></div>
              <ExternalMaterialRow
                :material="material.item"
                removable
                @remove="removeMaterial(material.externalMaterialId)"
              />
            </template>
          </div>
        </div>
      </div>

      <div
        class="mt-auto flex items-center justify-between gap-sm pt-md"
        :class="mobileViewportExpanded && 'min-h-0 flex-col flex-nowrap items-stretch gap-md'"
      >
        <div class="flex flex-wrap items-center gap-md">
          <span ref="materialCommandAnchorRef" class="inline-flex shrink-0">
            <ComposerChip
              :appearance="selectedMaterials.length ? 'accent' : 'ghost'"
              :label="$t('supportMeasureEntry.materials.appChip')"
              :expanded="!!materialPopoverRef?.isOpen"
              :disabled="isSaving"
              @click="toggleMaterialPopover"
            >
              <template #icon><ExternalMaterialIcon class="size-3 shrink-0" /></template>
            </ComposerChip>
          </span>
          <span v-if="canRateCompetences" class="h-6 w-px shrink-0 bg-surface-divider" aria-hidden="true" />
          <span v-if="canRateCompetences" ref="competenceCommandAnchorRef" class="inline-flex shrink-0">
            <ComposerChip
              :appearance="selectedCompetenceIds.length ? 'accent' : 'ghost'"
              :tint-color="competenceChipTintColor"
              :label="competenceChipLabel"
              :expanded="!!competencePopoverRef?.isOpen"
              :disabled="isSaving"
              @click="onCompetenceChipClick"
            >
              <template #icon><CompetencesIcon class="size-3 shrink-0" /></template>
            </ComposerChip>
          </span>
        </div>
        <ComposerActionRow
          :is-submitting="isSaving"
          :mobile-expanded="mobileViewportExpanded"
          :submit-label="isEditMode ? $t('general.save') : $t('supportMeasureEntry.addProtocolEntry')"
          @collapse="handleRequestCollapse"
          @submit="submit"
        >
          <template #submit-icon="{ mobileExpanded }">
            <ArrowDownIcon
              v-if="!isEditMode || mobileExpanded"
              class="size-icon-md"
              :class="mobileExpanded && 'rotate-180'"
            />
            <ArrowRightIcon v-else class="size-icon-md" />
          </template>
        </ComposerActionRow>
      </div>

      <DateSelectionPopover
        ref="datePopoverRef"
        :anchor-element="dateTriggerRef"
        :date="date"
        :date-picker-teleport="datePickerTeleport"
        @update:date="date = $event"
      />
    </ComposerPanel>

    <ExternalMaterialSelectionPopover
      ref="materialPopoverRef"
      :anchor-element="materialTriggerRef"
      :school-subject-id="schoolSubjectId"
      :school-class-id="schoolClassId"
      :selected-materials="selectedMaterials"
      :mobile-drawer="mobileViewportPresentation"
      @add:material="addMaterial"
      @remove:material="removeMaterial"
    />
    <CompetenceSelectionPopover
      ref="competencePopoverRef"
      :anchor-element="competenceTriggerRef"
      :available-competence-domains="subjectDomains"
      :all-school-subjects="allSchoolSubjects"
      :restricted-competence-ids="connectedCompetenceIds"
      :selected-competences="selectedCompetences"
      :hide-school-subjects="true"
      :mobile-drawer="mobileViewportPresentation"
      @add:competence="addCompetence"
      @remove:competence="removeCompetence"
    />
    <span class="sr-only" role="status" aria-live="polite">{{ announcement }}</span>
  </section>
</template>
